import { useCallback, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";

const LS_WIDTH_KEY = "mannaRelatedRailWidth";
const LS_COLLAPSED_KEY = "mannaRelatedRailCollapsed";
export const RELATED_MIN_W = 280;
export const RELATED_MAX_W = 480;
export const RELATED_DEFAULT_W = 320;
const DEBOUNCE_MS = 1000;

const prefsKey = ["user_ui_preferences"] as const;

interface RelatedRailPrefs {
  related_rail_width_px: number | null;
  related_rail_collapsed: boolean | null;
  related_rail_sections: Record<string, boolean> | null;
}

function readLocalWidth(): number {
  if (typeof window === "undefined") return RELATED_DEFAULT_W;
  const raw = window.localStorage.getItem(LS_WIDTH_KEY);
  const n = raw ? Number.parseInt(raw, 10) : NaN;
  if (Number.isFinite(n) && n >= RELATED_MIN_W && n <= RELATED_MAX_W) return n;
  return RELATED_DEFAULT_W;
}

function readLocalCollapsed(): boolean {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(LS_COLLAPSED_KEY) === "1";
}

function clamp(n: number): number {
  if (n < RELATED_MIN_W) return RELATED_MIN_W;
  if (n > RELATED_MAX_W) return RELATED_MAX_W;
  return n;
}

function usePrefs() {
  return useQuery({
    queryKey: prefsKey,
    queryFn: async (): Promise<RelatedRailPrefs | null> => {
      const { data, error } = await supabase
        .from("user_ui_preferences_view")
        .select("related_rail_width_px, related_rail_collapsed, related_rail_sections")
        .maybeSingle();
      if (error) throw error;
      return data as RelatedRailPrefs | null;
    },
    staleTime: 5 * 60_000,
  });
}

function useUpsert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<RelatedRailPrefs>) => {
      const { error } = await supabase.rpc("upsert_user_ui_preferences", {
        payload: payload as never,
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: prefsKey }),
  });
}

export function useRelatedRailWidth() {
  const { data } = usePrefs();
  const upsert = useUpsert();
  const [overrideWidth, setOverrideWidth] = useState<number | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const widthPx =
    overrideWidth ??
    (data?.related_rail_width_px != null ? clamp(data.related_rail_width_px) : readLocalWidth());

  const setWidthPx = useCallback(
    (next: number) => {
      const w = clamp(Math.round(next));
      setOverrideWidth(w);
      if (typeof window !== "undefined") {
        window.localStorage.setItem(LS_WIDTH_KEY, String(w));
      }
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        upsert.mutate({ related_rail_width_px: w });
      }, DEBOUNCE_MS);
    },
    [upsert],
  );

  return {
    widthPx,
    setWidthPx,
    isResizing,
    setIsResizing,
    MIN_W: RELATED_MIN_W,
    MAX_W: RELATED_MAX_W,
    DEFAULT_W: RELATED_DEFAULT_W,
  };
}

export function useRelatedRailSections() {
  const { data } = usePrefs();
  const upsert = useUpsert();
  const [overrideMap, setOverrideMap] = useState<Record<string, boolean> | null>(null);

  const sections = overrideMap ?? data?.related_rail_sections ?? {};

  const setSectionExpanded = useCallback(
    (key: string, expanded: boolean) => {
      const next = { ...sections, [key]: expanded };
      setOverrideMap(next);
      upsert.mutate({ related_rail_sections: next });
    },
    [sections, upsert],
  );

  return { sections, setSectionExpanded };
}

export function useRelatedRailCollapsed() {
  const { data } = usePrefs();
  const upsert = useUpsert();
  const [overrideCollapsed, setOverrideCollapsed] = useState<boolean | null>(null);

  const collapsed =
    overrideCollapsed ??
    (typeof data?.related_rail_collapsed === "boolean"
      ? data.related_rail_collapsed
      : readLocalCollapsed());

  const setCollapsed = useCallback(
    (next: boolean) => {
      setOverrideCollapsed(next);
      if (typeof window !== "undefined") {
        window.localStorage.setItem(LS_COLLAPSED_KEY, next ? "1" : "0");
      }
      upsert.mutate({ related_rail_collapsed: next });
    },
    [upsert],
  );

  return { collapsed, setCollapsed, toggle: () => setCollapsed(!collapsed) };
}
